import { UAParser } from 'ua-parser-js';
import { EventName } from '@/constants';
import { globals } from '@/globals';
import { getWebSocketURL, pageReload } from '@/helpers';
import { setRemoteDictionaries } from '@/helpers/dictionary';
import { successMessage } from '@/helpers/message';
import authService from '@/services/authService';
import eventBus from '@/services/eventBus';
import {
    connectWebSocket,
    disconnectWebSocket,
    getWebSocketReadyState,
    isWebSocketOpen,
    webSocketClientSendObj,
    webSocketClientSetUserData,
} from '@/services/webSocketClient';
import { useSystemStatusStore } from '@/stores/systemStatus';
import { useUserStore } from '@/stores/user';

export const useWebSocketStore = defineStore('webSocket', () => {
    const timerId = ref<ReturnType<typeof setInterval> | null>(null);
    const lastPongAt = ref(new Date());
    const lastWsCheckedAt = ref(new Date());

    function SOCKET_ONOPEN() {
        // Called by the websocket plugin when the native socket opens.
        // The native socket instance itself is owned by webSocketClient.
        console.log('The websocket is ready to use');

        const parser = new UAParser(window.navigator.userAgent);
        const pageUrl = window.location.toString();

        const data = {
            browser: parser.getBrowser().name + ' ' + parser.getBrowser().version,
            os: parser.getOS().name,
            pageUrl,
            pageCode: globals.router?.currentRoute.value.name ?? undefined,
        };

        sendObj({
            event: 'updateUserData',
            data,
        });
        webSocketClientSetUserData(data);

        // The server answers our periodic ping with a "pong" event.
        // lastPongAt is used by heartbeat/checkConnection to detect dead sockets.
        setLastPongAt(new Date());
        heartbeat();
    }

    function SOCKET_ONCLOSE() {
        // Called for the currently active socket only. webSocketClient filters
        // out stale close callbacks from previous connections.
        console.log('The websocket is closed');

        if (timerId.value) {
            clearInterval(timerId.value);
            console.log('Cleared setInterval in onclose #' + timerId.value);
            timerId.value = null;
        }
    }

    function SOCKET_ONERROR(event: any) {
        // Treat transport errors as disconnected and stop the heartbeat timer.
        // VueUse autoReconnect may still attempt to reconnect after this.
        console.error(event);

        if (timerId.value) {
            clearInterval(timerId.value);
            console.log('Cleared setInterval in onerror #' + timerId.value);
            timerId.value = null;
        }
    }

    function SOCKET_ONMESSAGE(message: any) {
        // Application protocol dispatcher. The transport layer has already
        // parsed raw websocket data into a JSON object by this point.
        switch (message.event) {
            case 'init': {
                // The backend can tell us that the token/session is no longer valid.
                // In that case reuse the normal logout flow so local state is cleaned.
                const userStore = useUserStore();
                console.log('webSocket init: ' + message.data.message);
                if (!message.data.isLogin && userStore.login) {
                    console.log('Your session is expired');
                    authService.logout();
                }

                break;
            }
            case 'pong':
                // Server response to our heartbeat ping.
                setLastPongAt(new Date());
                break;

            case 'reload':
                if (message.data.redirectToMain === 'true') {
                    /*router
                            .push({
                                name: 'feedLive',
                            })
                            .catch(() => reload())
                            .then(() => reload());*/
                } else {
                    // window.location.reload();
                    pageReload(message.data.page);
                }
                break;
            case 'logout':
                authService.logout();
                break;
            case 'information':
                successMessage(message.data.message ? message.data.message : message.data);
                break;
            case 'dictionary':
                setRemoteDictionaries(message.data.dictionary);
                break;
            case 'pageReload': {
                eventBus.emit(EventName.PageReload, { page: message.data.page });
                break;
            }
            case 'systemStatus': {
                const systemStatusStore = useSystemStatusStore();
                systemStatusStore.setSystemStatus(message.data);
                break;
            }
            case 'userPersonalData': {
                const systemStatusStore = useSystemStatusStore();
                systemStatusStore.setUserPersonalData(message.data);
                break;
            }
            case 'webSocketUsers': {
                const rows = Array.isArray(message.data) ? message.data : [];
                eventBus.emit(EventName.WebSocketUsers, rows);
                break;
            }
        }
    }

    function setLastPongAt(value: Date) {
        lastPongAt.value = value;
        console.log('Set lastPongAt = ' + value);
    }

    function heartbeat() {
        // Keep only one heartbeat interval. SOCKET_ONOPEN can be called again
        // after reconnect, so the previous interval must be cleared first.
        if (timerId.value) {
            clearInterval(timerId.value);
            console.log('Cleared setInterval in heartbeat #' + timerId.value);
            timerId.value = null;
        }
        timerId.value = setInterval(() => {
            let lastPongAtValue = lastPongAt.value;

            let currentDate = new Date();
            let diff = currentDate.getTime() - lastPongAtValue.getTime();
            let maxDiff = 60 * 1000;

            if (diff >= maxDiff) {
                // We have not received pong for too long. Close the current
                // socket and open a new one only if the user is still logged in.
                const readyState = getWebSocketReadyState();
                console.log(`The websocket should be closed due to timeout, state is ${readyState}. Diff = ${diff}, lastPongAt = ${lastPongAtValue}`);
                if (readyState !== undefined) {
                    const userStore = useUserStore();
                    disconnectWebSocket();

                    if (userStore.isLoggedIn()) {
                        const webSocketUrl = getWebSocketURL(userStore.token!);
                        connectWebSocket(webSocketUrl);
                    }
                }
            } else {
                // The connection looks alive enough; send ping and wait for
                // SOCKET_ONMESSAGE("pong") to refresh lastPongAt.
                console.log('Ping. lastPongAt = ' + lastPongAtValue);
                sendObj({
                    event: 'ping',
                });
            }
        }, 15000); // 15 seconds

        console.log('Added setInterval #' + timerId.value);
    }

    function sendObj(obj: object): boolean {
        return webSocketClientSendObj(obj);
    }

    function checkConnection() {
        // Throttle manual checks. App.vue can call this from visibility/focus
        // events, and repeated checks would otherwise close/reopen too often.
        let currentDate = new Date();
        let diff = (currentDate.getTime() - lastWsCheckedAt.value.getTime()) / 1000;

        if (diff < 5) {
            console.log(`Skip checking WS connection, last check was ${diff} sec ago`);
            return;
        }

        lastWsCheckedAt.value = new Date();

        let wsDiff = currentDate.getTime() - lastPongAt.value.getTime();
        const readyState = getWebSocketReadyState();
        if (isWebSocketOpen() && wsDiff < 60 * 1000) {
            console.log(`WS connections is ok, last pong was ${wsDiff}ms ago`);
        } else {
            // The native socket is missing/not open or pong is too old.
            // Reconnect only for an authenticated user; logout should leave
            // the websocket closed.
            const userStore = useUserStore();
            if (!userStore.isLoggedIn()) return;

            console.log(`WS connections is failed, readyState is ${readyState}, last pong was ${wsDiff}ms ago`);

            disconnectWebSocket();
            const webSocketUrl = getWebSocketURL(userStore.token!);
            connectWebSocket(webSocketUrl);
        }
    }

    return {
        timerId,
        lastPongAt,
        lastWsCheckedAt,
        SOCKET_ONOPEN,
        SOCKET_ONCLOSE,
        SOCKET_ONERROR,
        SOCKET_ONMESSAGE,
        checkConnection,
        sendObj,
    };
});
